// The user's diet setting, client side.
//
// The vocabulary itself lives on the server (back-end/schedule/diet.py) and
// arrives with the value from GET /api/users/me/preferences, so the labels a
// picker shows and the families a setting excludes are defined once. Four
// surfaces read this — profile, onboarding, the schedule builder's per-build
// override and the browse page's default filter — and a local copy of "what
// vegan means" is how those four end up disagreeing.

import { useCallback, useEffect, useState } from "react";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";

export const DIET_NONE = "none";

// Labels only, for the case where the preferences request fails. Enough to
// render a working control; deliberately NOT carrying excluded_groups, because
// a stale guess at which families a diet covers would hide entries from the
// avoid picker for the wrong reason. With an empty list that picker simply
// degrades to offering everything, which is what it did before this existed.
export const FALLBACK_DIET_OPTIONS = [
  {
    value: DIET_NONE,
    label: "No preference",
    description: "Everything in the catalog",
    excluded_groups: [],
  },
  {
    value: "vegetarian",
    label: "Vegetarian",
    description: "No meat, poultry or seafood",
    excluded_groups: [],
  },
  { value: "vegan", label: "Vegan", description: "No animal products at all", excluded_groups: [] },
];

// What a *recipe's* own `diet` label reads. A separate vocabulary from the
// settings one above and deliberately so: a user is "vegetarian" or has "no
// preference", a recipe is "vegan" | "vegetarian" | "meat" and never "none".
// Mirrors the values back-end/schedule/diet.py filters on — the filter is the
// only promise the app makes about diet, so the same three words have to be
// readable on the recipe itself.
export const RECIPE_DIET_LABELS = { vegan: "Vegan", vegetarian: "Vegetarian", meat: "Meat" };

// Colour per label, matching the meal-type chips' weight: a quiet tint, not a
// warning. Meat is the neutral one — it is the catalog's default, not a flag.
export const RECIPE_DIET_STYLE = {
  vegan: { color: "#2e7d32", bg: "#e8f5e9" },
  vegetarian: { color: "#558b2f", bg: "#f1f8e9" },
  meat: { color: "#8d6e63", bg: "#efebe9" },
};

/** The chip text for a recipe's diet label, or null when it carries none. */
export function recipeDietLabel(diet) {
  return diet ? RECIPE_DIET_LABELS[diet] || diet : null;
}

/** The value browse's exact-match diet filter wants: null for no restriction. */
export function dietFilterValue(diet) {
  return !diet || diet === DIET_NONE ? null : diet;
}

/** The ingredient-group slugs a setting already covers, from the options list. */
export function excludedGroupsFor(diet, options) {
  const match = (options || []).find((o) => o.value === diet);
  return match ? match.excluded_groups || [] : [];
}

/**
 * Read (and optionally write) the stored diet setting.
 *
 * `saveDiet` PATCHes only the diet field — the unit toggle owns its own and the
 * two must not overwrite each other. It updates local state first so the chips
 * respond immediately, and rolls back if the write fails: a control that looks
 * saved but is not is worse than one that visibly refuses.
 *
 * It resolves to `{ ok, goalsReplaced }`. Changing a diet can take back goals
 * it makes unwinnable — a vegetarian does not keep "Chicken Whisperer" — and
 * the count is surfaced so the change can be announced. A goal disappearing
 * from the dashboard with nothing said about it reads as a bug.
 */
export function useDietPreference() {
  const [diet, setDiet] = useState(DIET_NONE);
  const [options, setOptions] = useState(FALLBACK_DIET_OPTIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get(ENDPOINTS.users.preferences)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.diet) setDiet(res.data.diet);
        if (Array.isArray(res.data?.diet_options) && res.data.diet_options.length > 0) {
          setOptions(res.data.diet_options);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveDiet = useCallback(
    async (next) => {
      const previous = diet;
      setDiet(next);
      try {
        const res = await apiClient.patch(ENDPOINTS.users.preferences, { diet: next });
        const changed = res.data?.goals_changed || [];
        return { ok: true, goalsReplaced: changed.filter((c) => c.dropped).length };
      } catch (err) {
        setDiet(previous);
        return { ok: false, goalsReplaced: 0 };
      }
    },
    [diet]
  );

  return { diet, options, loading, saveDiet, setDiet };
}
